// Shared helpers for the email composers (new / reply / reply-all / forward)

// ── Quoted / forwarded content handling ──────────────────────────────────
// The original email (when replying/forwarding) is kept as raw HTML OUTSIDE the
// TipTap editor (TipTap's schema strips tables/styles/images). We wrap it in a
// sentinel div so drafts can be split back into "user message" + "quote".
const QUOTE_OPEN = '<div data-mf-quote="1">';
const QUOTE_CLOSE = '</div><!--/mf-quote-->';

export function wrapQuote(quoteHtml: string): string {
  if (!quoteHtml) return '';
  return QUOTE_OPEN + quoteHtml + QUOTE_CLOSE;
}

// Split a stored body back into the editable user part and the quoted part.
export function splitQuote(html: string): { userHtml: string; quoteHtml: string } {
  if (!html) return { userHtml: '', quoteHtml: '' };
  const start = html.indexOf(QUOTE_OPEN);
  if (start === -1) return { userHtml: html, quoteHtml: '' };
  const userHtml = html.slice(0, start);
  let quoteHtml = html.slice(start + QUOTE_OPEN.length);
  const end = quoteHtml.indexOf(QUOTE_CLOSE);
  if (end !== -1) quoteHtml = quoteHtml.slice(0, end);
  return { userHtml, quoteHtml };
}

// ── The outgoing body carries the editor's own typography ────────────────────
// The composer's editor takes its type from the app's own stylesheet (.ProseMirror in
// styles/global.css): the UI sans face at 13px, 1.75 line spacing, 6px between paragraphs. A
// stylesheet does not travel with an email, so the preview iframe and the recipient's mail program
// both fell back to their own default — a serif face at browser size with roughly 1.2 line spacing
// (report 6e823c08: "When I am composing the email it looks good. However when I click the preview,
// the preview looks terrible"). Everything the message needs in order to keep the editor's shape is
// therefore written as inline style, here at the one point where the body is put together, so the
// preview, a Send Now and a scheduled send are all the same email.
//
// The font is the app's own default, Google Sans (report 806823ce), with web-safe fallbacks: a sheet does not travel
// with an email, so a recipient without Google Sans on their machine gets Helvetica or Arial, the same sans-serif.
const BODY_FONT = `'Google Sans', Helvetica, Arial, sans-serif`;
const BODY_SIZE = '13px';       // .ProseMirror font-size
const BODY_LINE = '1.75';       // .ProseMirror line-height
const BODY_COLOR = '#1A1917';   // --text-primary — the colour the operator types in
const PARA_GAP = '6px';         // .ProseMirror p { margin-bottom: 6px }

const BODY_STYLE = `font-family:${BODY_FONT};font-size:${BODY_SIZE};line-height:${BODY_LINE};color:${BODY_COLOR}`;

// The editor's paragraph rhythm, inline: nothing above a paragraph, PARA_GAP below it. A mail
// program's own default is about one line's worth on BOTH sides, which is what pushed the preview's
// paragraphs apart while squeezing the lines inside them together. Written as longhands when the
// paragraph already carries a style, so an indented paragraph keeps its margin-left.
function spaceParagraphs(html: string): string {
  return String(html || '').replace(/<p(\s[^>]*)?>/gi, (tag: string, attrs?: string) => {
    const a = attrs || '';
    if (!/\sstyle\s*=/i.test(a)) return `<p${a} style="margin:0 0 ${PARA_GAP}">`;
    const m = a.match(/\sstyle\s*=\s*"([^"]*)"/i);
    if (!m) return tag;   // a style attribute this cannot read — leave the paragraph untouched
    // Replaced through a function, so a `$` anywhere in the operator's own style is not read as one
    // of String.replace's substitution patterns.
    return `<p${a.replace(m[0], () => ` style="margin-top:0;margin-bottom:${PARA_GAP};${m[1]}"`)}>`;
  });
}

/**
 * The body of an outgoing email: the operator's message, their signature, then the quoted or
 * forwarded original — all inside one container that carries the editor's typography. Pass the
 * message alone to see just the message in that same shape (the translation preview does).
 * The editor's paragraph rhythm is applied to the message only: a signature and a quoted original
 * are somebody else's HTML and keep the spacing they came with.
 */
export function buildOutgoingBody(message: string, signature?: string, quoteHtml?: string): string {
  const sigBlock = signature ? `<hr style="border:none;border-top:1px solid #eee;margin:16px 0">${signature}` : '';
  const quoteBlock = quoteHtml ? `<br><br>${quoteHtml}` : '';
  return `<div style="${BODY_STYLE}">${spaceParagraphs(message)}${sigBlock}${quoteBlock}</div>`;
}

// Client-side stable id generator (used for draft ids and attachment keys)
export function genId(): string {
  try {
    if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) {
      return (crypto as any).randomUUID();
    }
  } catch {}
  return 'id-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// Insert an image node directly via the ProseMirror view (no TipTap editor ref needed)
function insertImageNode(view: any, dataUrl: string) {
  try {
    const { state } = view;
    const imageType = state.schema.nodes.image;
    if (!imageType) return;
    const node = imageType.create({ src: dataUrl });
    const tr = state.tr.replaceSelectionWith(node);
    view.dispatch(tr);
  } catch {
    /* ignore */
  }
}

/**
 * Build TipTap `editorProps` paste/drop handlers that:
 *  - insert pasted/dropped IMAGES inline (as base64) into the editor
 *  - add pasted/dropped non-image FILES to the attachment list
 * Works for both Ctrl/Cmd+V and the browser's native right-click "Paste"
 * (the OS context menu is auto-localized, so all languages are supported).
 */
export function makePasteHandlers(addAttachment: (file: File) => void) {
  const processFiles = (view: any, files: File[]): boolean => {
    if (!files.length) return false;
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = ev => insertImageNode(view, ev.target?.result as string);
        reader.readAsDataURL(file);
      } else {
        addAttachment(file);
      }
    });
    return true;
  };

  return {
    handlePaste: (view: any, event: ClipboardEvent) => {
      const files = event.clipboardData?.files;
      if (files && files.length > 0) {
        event.preventDefault();
        return processFiles(view, Array.from(files));
      }
      return false; // let ProseMirror handle text/html normally
    },
    handleDrop: (view: any, event: DragEvent) => {
      const files = event.dataTransfer?.files;
      if (files && files.length > 0) {
        event.preventDefault();
        return processFiles(view, Array.from(files));
      }
      return false;
    },
  };
}
